import { createContext, useMemo, useRef } from "react";
import rosetta from "rosetta";

// import rosetta from 'rosetta/debug';
import translations from "./translations";

const i18n = rosetta(translations);

export const defaultLanguage = "de";
export const languages = ["de", "en", "fr", "it"];
export const contentLanguageMap = {
  de: "de-DE",
  en: "en-US",
  fr: "fr-FR",
  it: "it-IT",
};

export const I18nContext = createContext<I18nObject>({
  activeLocale: defaultLanguage,
  locale: () => {
    return defaultLanguage;
  },
  t: (id: string) => {
    return id;
  },
});

// default language
i18n.locale(defaultLanguage);

export interface I18nProps {
  children: React.ReactNode;
  lngDict?: Record<string, string>;
  locale?: string;
}

export interface I18nObject {
  activeLocale: string;
  locale: (lang?: string) => string;
  t: (id: string, values?: Record<string, unknown>, lang?: string) => string;
}

function I18n({ children, locale }: I18nProps) {
  const activeLocale = useRef<string>(i18n.locale());

  const i18nWrapper: I18nObject = useMemo(() => {
    i18n.locale(locale || defaultLanguage);
    return {
      activeLocale: activeLocale.current,
      locale: (lang?: string): string => {
        if (lang && lang !== i18n.locale()) {
          i18n.locale(lang);
          activeLocale.current = lang;
        }
        return i18n.locale();
      },
      t: (id: string, values?: Record<string, unknown>, lang?: string) => {
        if (typeof id !== "string") {
          // eslint-disable-next-line no-console
          console.warn("i18n.t called with non-string id:", id);
          return "";
        }
        const translation = i18n.t(id, values, lang);
        if (!translation) {
          return id;
        }
        return translation;
      },
    };
  }, [locale]);

  return (
    <I18nContext.Provider value={i18nWrapper}>{children}</I18nContext.Provider>
  );
}

export default I18n;
